import React, { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { z } from "zod";
import { useForm } from "react-hook-form";
import { updateMemberSchema } from "@/utils/schemaHelper";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/uiComponents/components/ui/form";
import { Input } from "@/uiComponents/components/ui/input";

import { useDispatch, useSelector } from "react-redux";

import { ChevronDown } from "lucide-react";
import { RootState } from "@/store/store";
import { setLoading } from "@/store/redux/appSlice";
import { getMembersApi, updateMemberApi } from "@/modules/organizationServices";

import { MemberErrors } from "@/enums/errorMessages";

import { updateMemberProps } from "@/types/organization";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/uiComponents/components/ui/dialog";

import { setMembers } from "@/store/redux/organization/memberSlice";
import { ErrorHelper } from "@/utils/errorHelper";
import { toastMessage } from "@/utils/toastHelper";
import { roleLabels, roles } from "@/enums/roles";
import { ReusableButton } from "@/reusable/reusableButton";

const UpdateMember: React.FC<updateMemberProps> = ({
  onClose,
  isOpen,
  member,
}) => {
  if (!isOpen) return null;
  const dispatch = useDispatch();

  const loading = useSelector((state: RootState) => state.app.loading);
  const userDetails = useSelector(
    (state: RootState) => state.userDetail.userDetail,
  );
  const branches = useSelector((state: RootState) => state.branch.branches);
  const orgId = userDetails?.organizationId;

  const role = useSelector((state: RootState) => state.userDetail.role);
  const roleDropRef = useRef<HTMLDivElement>(null);
  const branchDropRef = useRef<HTMLDivElement>(null);
  const [isBranchOpen, setIsBranchOpen] = useState<boolean>(false);
  const [isRoleOpen, setIsRoleOpen] = useState<boolean>(false);

  const form = useForm<z.infer<typeof updateMemberSchema>>({
    resolver: zodResolver(updateMemberSchema),
    mode: "onChange",
    defaultValues: {
      username: member.username,
      email: member.email,
      role: member.role,
      organization: orgId,
      branch: member.branchId,
    },
  });
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        branchDropRef.current &&
        !branchDropRef.current.contains(e.target as Node)
      ) {
        setIsBranchOpen(false);
      }
      if (
        roleDropRef.current &&
        !roleDropRef.current.contains(e.target as Node)
      ) {
        setIsRoleOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const formRole = form.watch("role");
  useEffect(() => {
    if (formRole !== "member") {
      form.setValue("branch", null);
    }
  }, [formRole, form]);

  const getMembers = async () => {
    try {
      const res = await getMembersApi();
      const response = res.users;
      if (response != undefined) {
        dispatch(setMembers(response));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  const onSubmit = async (values: z.infer<typeof updateMemberSchema>) => {
    if (loading) return;
    dispatch(setLoading(true));

    try {
      if (!member.id) {
        throw new Error(MemberErrors.MEMBER_ID_REQUIRED);
        toastMessage("error", MemberErrors.MEMBER_ID_REQUIRED);
      }

      await updateMemberApi(member.id, values, role);

      getMembers();

      onClose();

      toastMessage("success", MemberErrors.MEMBER_UPDATED);
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Member</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6 mt-5"
          >
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>

                  <FormControl>
                    <Input
                      className="bg-white"
                      readOnly
                      {...field}
                      placeholder="Member Name"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="max-h-13 ">
                  <FormLabel>Email</FormLabel>

                  <FormControl>
                    <Input className="bg-white" readOnly {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem className="max-h-13">
                  <FormLabel>Role</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <div className="relative" ref={roleDropRef}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsRoleOpen(!isRoleOpen);
                          }}
                          className="w-full border p-2 bg-white rounded-md text-left flex items-center justify-between"
                        >
                          {field.value === roles.ORG_ADMIN
                            ? roleLabels.ORG_ADMIN
                            : field.value === roles.MEMBER
                              ? roleLabels.MEMBER
                              : "Select Role"}
                          <ChevronDown
                            size={16}
                            className={`transition-transform duration-200 ${
                              isRoleOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        {isRoleOpen && (
                          <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg">
                            <div
                              className={`p-2 hover:bg-gray-100 cursor-pointer ${field.value == roles.ORG_ADMIN ? "bg-blue-300" : ""}`}
                              onClick={() => {
                                field.onChange(roles.ORG_ADMIN);
                                setIsRoleOpen(false);
                              }}
                            >
                              {roleLabels.ORG_ADMIN}
                            </div>
                            <div
                              className={`p-2 hover:bg-gray-100 cursor-pointer ${field.value === roles.MEMBER ? "bg-blue-300" : ""}`}
                              onClick={() => {
                                field.onChange(roles.MEMBER);
                                setIsRoleOpen(false);
                              }}
                            >
                              {roleLabels.MEMBER}
                            </div>
                          </div>
                        )}
                      </div>
                    </FormControl>
                  </div>

                  <div className="min-h-6">
                    <FormMessage className="text-xs" />
                  </div>
                </FormItem>
              )}
            />

            {form.watch("role") == "member" && (
              <FormField
                name="branch"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Branch</FormLabel>
                    <FormControl>
                      <div className="relative" ref={branchDropRef}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsBranchOpen(!isBranchOpen);
                          }}
                          className="w-full bg-white border p-2 rounded-md text-left flex items-center justify-between"
                        >
                          {field.value
                            ? branches.find((b) => b.id === field.value)
                                ?.branchName
                            : "Select a branch"}
                          <ChevronDown
                            size={16}
                            className={`transition-transform duration-200 ${
                              isBranchOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        {isBranchOpen && (
                          <ul className="absolute left-0 mt-1 w-full bg-white border rounded-md shadow z-50 max-h-60 overflow-y-auto">
                            {branches.map((branch) => (
                              <li
                                key={branch.id}
                                onClick={() => {
                                  field.onChange(branch.id);
                                  setIsBranchOpen(false);
                                }}
                                className={`p-2 hover:bg-gray-100 cursor-pointer ${field.value == branch.id ? "bg-blue-300" : ""}`}
                              >
                                {branch.branchName}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="flex justify-center mt-4 mr-2">
              <ReusableButton
                label="Update"
                className=" mt-5 mb-2 "
                loading={loading}
              />
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
export default UpdateMember;
